import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Tabs from '@mui/material/Tabs';
import { useContext } from 'react';

import { EmployeesTable } from './employee-tables/EmployeesTable';
import { JobOpeningsTable } from './job-openings-tables/JobOpeningsTable';
import { LearningOpportunitiesTable } from './learning-opportuinities-tables/LearningOpportunitiesTable';
import { SkillsTable } from './skills-tables/SkillsTable';
import { useFilterTabsByPermission } from './use-filter-tabs-by-permission';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { ContentHeader, ContentWrapper } from '../../../design/components/containers/ContentWrapper';
import { TabWithBadge } from '../../../design/components/tabs/TabWithBadge';
import { useNavigationTabs } from '../../common/use-navigation-tab';
import { ComponentErrorBoundary } from '../../error-handling/ErrorBoundaries';
import { useGlobalSearchTableParams } from '../../global-search/use-global-search-table-params';
import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import {
    useGlobalSearchEmployeeCountBySkillIds,
    useGlobalSearchJobsCountBySkillsIds,
    useGlobalSearchLearningOpportunitiesCountBySkillIds,
    useGlobalSearchSearchTermCountQueries,
    useGlobalSearchSkillsCountBySkillIds,
} from '../../query-hooks/global-search/count';

import type { FunctionComponent } from 'react';

type TabKey = 'employees' | 'jobs' | 'learningOpportunities' | 'skills';

const useGlobalSearchSkillIdsCounter = (skillIds: readonly string[]): Record<TabKey, number> => {
    const { data: globalPermissions } = useGlobalPermissions();
    const canUserGetSkillsCount = globalPermissions?.canSearchSkills ?? false;
    const canUserGetEmployeesCount = globalPermissions?.canSearchUsers ?? false;
    const canUserGetJobsCount = globalPermissions?.canSearchJobs ?? false;
    const canUserGetLearningOpportunitiesCount = globalPermissions?.canSearchLearningOpportunities ?? false;

    const globalSearchEmployeesSkillIdsQueryCount = useGlobalSearchEmployeeCountBySkillIds({
        enabled: canUserGetEmployeesCount,
        skillIds,
    });
    const globalSearchJobsSkillIdsQueryCount = useGlobalSearchJobsCountBySkillsIds({
        enabled: canUserGetJobsCount,
        skillIds,
    });
    const globalSearchSkillIdsQueryCount = useGlobalSearchSkillsCountBySkillIds({
        enabled: canUserGetSkillsCount,
        skillIds,
    });
    const useGlobalSearchLearningOpportunitiesSkillIdsQueryCount = useGlobalSearchLearningOpportunitiesCountBySkillIds({
        enabled: canUserGetLearningOpportunitiesCount,
        skillIds,
    });

    const globalSearchEmployeesSkillIdsCount = globalSearchEmployeesSkillIdsQueryCount.data?.pagination?.count ?? 0;
    const globalSearchJobsSkillIdsCount = globalSearchJobsSkillIdsQueryCount.data?.pagination?.count ?? 0;
    const globalSearchSkillIdsCount = globalSearchSkillIdsQueryCount.data?.relatedSkillData.pagination?.count ?? 0;
    const globalSearchLearningOpportunitiesSkillIdsCount =
        useGlobalSearchLearningOpportunitiesSkillIdsQueryCount.data?.pagination?.count ?? 0;

    return {
        employees: globalSearchEmployeesSkillIdsCount,
        jobs: globalSearchJobsSkillIdsCount,
        learningOpportunities: globalSearchLearningOpportunitiesSkillIdsCount,
        skills: globalSearchSkillIdsCount,
    };
};

const getBadgeValue = (label: number): string | undefined => (label > 0 ? label.toString() : undefined);

const SearchTermTabs: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const permittedTabs = useFilterTabsByPermission();
    const { currentTabIndex } = useNavigationTabs(permittedTabs);
    const { getSearchParamsForTab, searchTermResult } = useGlobalSearchTableParams();

    const [skillsCount, employeesCount, jobsCount, learningOpportunitiesCount] =
        useGlobalSearchSearchTermCountQueries(searchTermResult);

    const globalSearchPageCounter: Record<TabKey, number> = {
        employees: employeesCount?.data?.pagination?.count ?? 0,
        jobs: jobsCount?.data?.pagination?.count ?? 0,
        learningOpportunities: learningOpportunitiesCount?.data?.pagination?.count ?? 0,
        skills: skillsCount?.data?.pagination?.count ?? 0,
    };

    return (
        <Tabs aria-label="table" value={currentTabIndex}>
            {permittedTabs.map(({ key, translationKey }) => (
                <TabWithBadge
                    key={key}
                    badgeLabel={getBadgeValue(globalSearchPageCounter[key])}
                    label={translate(translationKey)}
                    replace={false}
                    to={getSearchParamsForTab(key)}
                />
            ))}
        </Tabs>
    );
};

const SkillIdsTabs: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const permittedTabs = useFilterTabsByPermission();
    const { currentTabIndex } = useNavigationTabs(permittedTabs);
    const { getSearchParamsForTab, skillIds } = useGlobalSearchTableParams();

    const globalSearchPageCounter = useGlobalSearchSkillIdsCounter(skillIds);

    return (
        <Tabs aria-label="table" value={currentTabIndex}>
            {permittedTabs.map(({ key, translationKey }) => (
                <TabWithBadge
                    key={key}
                    badgeLabel={getBadgeValue(globalSearchPageCounter[key])}
                    label={translate(translationKey)}
                    replace={false}
                    to={getSearchParamsForTab(key)}
                />
            ))}
        </Tabs>
    );
};

const TableHeadWithTabs: FunctionComponent = () => {
    const { isValidSearchTerm, isValidSkillIdsList } = useGlobalSearchTableParams();
    const { translate } = useContext(ApplicationContext);

    if (isValidSkillIdsList) {
        return <SkillIdsTabs />;
    }

    if (isValidSearchTerm) {
        return <SearchTermTabs />;
    }

    return <Alert severity="error">{translate('validation.search-term')}</Alert>;
};

export const SearchTable: FunctionComponent = () => {
    const permittedTabs = useFilterTabsByPermission();
    const { currentTab } = useNavigationTabs(permittedTabs);
    const currentTabKey = currentTab.get('key').unwrapOr('');

    return (
        <TableContainer
            className="global-search-tabs"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'stretch',
            }}
        >
            <ContentWrapper>
                <ContentHeader>
                    <TableHeadWithTabs />
                </ContentHeader>
                <Grid>
                    {currentTabKey === 'skills' && (
                        <ComponentErrorBoundary message="errors.component.global-search.skills">
                            <SkillsTable />
                        </ComponentErrorBoundary>
                    )}
                    {currentTabKey === 'employees' && (
                        <ComponentErrorBoundary message="errors.component.global-search.employees">
                            <EmployeesTable />
                        </ComponentErrorBoundary>
                    )}
                    {currentTabKey === 'jobs' && (
                        <ComponentErrorBoundary message="errors.component.global-search.job-openings">
                            <JobOpeningsTable />
                        </ComponentErrorBoundary>
                    )}
                    {currentTabKey === 'learningOpportunities' && (
                        <ComponentErrorBoundary message="errors.component.global-search.learning-opportunities">
                            <LearningOpportunitiesTable />
                        </ComponentErrorBoundary>
                    )}
                </Grid>
            </ContentWrapper>
        </TableContainer>
    );
};
